'use client';

import * as React from 'react';

import { FileDropzone, Typography, UploadProgress } from '../../atoms';

export type FileUploadProps = React.HTMLAttributes<HTMLDivElement> & {
  isLoading: number | undefined;
  error: string | undefined;
  onFileChosen: (dropItems: File) => void;
  templatePath?: string;
  acceptedFileTypes?: string[];
  showTemplateLink?: boolean;
  dataTestId?: string;
  templateDataTestId?: string;
};

export const FileUpload = React.forwardRef<HTMLDivElement, FileUploadProps>(
  (
    {
      templatePath = '/patient-list-upload-template.csv',
      acceptedFileTypes = ['xls', 'xlsx', 'csv'],
      showTemplateLink = true,
      isLoading,
      error,
      onFileChosen,
      ...props
    },
    ref,
  ) => {
    return (
      <div ref={ref} className="w-full space-y-4" {...props}>
        <section>
          <Typography className="text-onLight-high" variant="h4">
            Upload Your File
          </Typography>
          <Typography className="text-onLight-medium mt-1" variant="body4">
            Accepted document file{' '}
            {acceptedFileTypes.length > 1 ? 'types' : 'type'}:{' '}
            {acceptedFileTypes.join(', ')}.{' '}
            {showTemplateLink && (
              <>
                <a
                  className="text-controls-primary-office font-semibold"
                  href={templatePath}
                  data-testid={props.templateDataTestId}
                >
                  Download our template
                </a>{' '}
                <span> for easy uploading.</span>
              </>
            )}
          </Typography>
        </section>
        {isLoading ? (
          <UploadProgress
            progress={isLoading}
            messageDuringProgress="Uploading your file and checking for errors."
          />
        ) : (
          <FileDropzone
            error={error ? error : ''}
            onFileChosen={onFileChosen}
            dataTestId={props.dataTestId}
          />
        )}
      </div>
    );
  },
);

FileUpload.displayName = 'FileUpload';
